import { useEffect, useRef } from "react";
import {
  ArrowLeft,
  CreditCard,
  Globe,
  Palette,
  ShieldAlert,
  Trash2,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Text } from "../../ui";
import { cn } from "../../../lib/cn";
import type { Workspace } from "../../../lib/api";
import { useKnowledgeNavigationStore } from "../../../stores/knowledgeNavigationStore";
import { AppearanceSection } from "./AppearanceSection";
import { PlanSection } from "./PlanSection";
import { PublicSiteSection } from "./PublicSiteSection";
import { useSettingsLayout } from "./SettingsLayoutContext";
import { TeamSection } from "./TeamSection";
import { DangerSection } from "./DangerSection";
import { TrashSection } from "./TrashSection";

export const SETTINGS_SECTIONS = [
  "public-site",
  "appearance",
  "team",
  "trash",
  "plan",
  "danger",
] as const;

export type SettingsSectionId = (typeof SETTINGS_SECTIONS)[number];

type NavItem = {
  id: SettingsSectionId;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
};

const navItems: NavItem[] = [
  { id: "public-site", label: "Public site", icon: Globe },
  { id: "appearance", label: "Appearance", icon: Palette },
  { id: "team", label: "Team", icon: Users },
  { id: "trash", label: "Trash", icon: Trash2, adminOnly: true },
  { id: "plan", label: "Plan", icon: CreditCard },
  { id: "danger", label: "Danger zone", icon: ShieldAlert, adminOnly: true },
];

type KnowledgeSettingsShellProps = {
  workspace: Workspace;
  workspaceId: string;
  isAdmin: boolean;
  activeSection: SettingsSectionId;
};

function SettingsNavLink({
  item,
  workspaceId,
  activeSection,
  onNavigate,
  className,
}: {
  item: NavItem;
  workspaceId: string;
  activeSection: SettingsSectionId;
  onNavigate?: () => void;
  className?: string;
}) {
  const Icon = item.icon;
  const active = activeSection === item.id;

  return (
    <Link
      to="/knowledge/$workspaceId/settings"
      params={{ workspaceId }}
      search={{ section: item.id }}
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
        active
          ? "font-semibold text-foreground"
          : "text-foreground/40 hover:text-foreground",
        className,
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {item.label}
    </Link>
  );
}

function ReturnToEditorButton({
  workspaceId,
  onNavigate,
}: {
  workspaceId: string;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const startTransition = useKnowledgeNavigationStore((s) => s.startTransition);

  return (
    <Button
      variant="primary"
      size="sm"
      fullWidth={false}
      className="w-fit"
      leftIcon={<ArrowLeft className="h-4 w-4" />}
      onClick={() => {
        startTransition("editor");
        navigate({ to: "/knowledge/$workspaceId", params: { workspaceId } });
        onNavigate?.();
      }}
    >
      Return to editor
    </Button>
  );
}

function SettingsNav({
  workspaceId,
  activeSection,
  isAdmin,
  onNavigate,
  className,
}: {
  workspaceId: string;
  activeSection: SettingsSectionId;
  isAdmin: boolean;
  onNavigate?: () => void;
  className?: string;
}) {
  const visibleItems = navItems.filter((item) => !item.adminOnly || isAdmin);

  return (
    <nav className={cn("flex flex-col gap-1", className)}>
      {visibleItems.map((item) => (
        <SettingsNavLink
          key={item.id}
          item={item}
          workspaceId={workspaceId}
          activeSection={activeSection}
          onNavigate={onNavigate}
        />
      ))}
    </nav>
  );
}

function renderSection(
  section: SettingsSectionId,
  workspace: Workspace,
  workspaceId: string,
  isAdmin: boolean,
) {
  switch (section) {
    case "public-site":
      return <PublicSiteSection workspace={workspace} isAdmin={isAdmin} />;
    case "appearance":
      return <AppearanceSection workspace={workspace} isAdmin={isAdmin} />;
    case "team":
      return <TeamSection workspace={workspace} isAdmin={isAdmin} />;
    case "trash":
      return isAdmin ? <TrashSection workspaceId={workspaceId} /> : null;
    case "plan":
      return <PlanSection workspace={workspace} />;
    case "danger":
      return isAdmin ? <DangerSection workspace={workspace} /> : null;
    default:
      return <PublicSiteSection workspace={workspace} isAdmin={isAdmin} />;
  }
}

export function KnowledgeSettingsShell({
  workspace,
  workspaceId,
  isAdmin,
  activeSection,
}: KnowledgeSettingsShellProps) {
  const navigate = useNavigate();
  const { drawerOpen, setDrawerOpen } = useSettingsLayout();
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAdmin && (activeSection === "trash" || activeSection === "danger")) {
      navigate({
        to: "/knowledge/$workspaceId/settings",
        params: { workspaceId },
        search: { section: "public-site" },
        replace: true,
      });
    }
  }, [activeSection, isAdmin, navigate, workspaceId]);

  useEffect(() => {
    if (!drawerOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setDrawerOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    drawerRef.current?.focus();
  }, [drawerOpen]);

  function closeDrawer() {
    setDrawerOpen(false);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col md:flex-row">
      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="Close settings menu"
            className="absolute inset-0 bg-foreground/40"
            onClick={closeDrawer}
          />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Settings navigation"
            tabIndex={-1}
            className={cn(
              "absolute inset-y-0 left-0 flex w-[min(18rem,85vw)] flex-col bg-white px-4 py-6 shadow-xl outline-none",
              "translate-x-0 transition-transform duration-200 ease-out",
            )}
          >
            <Text variant="label" className="mb-4 px-3 text-foreground/50">
              Settings
            </Text>
            <ReturnToEditorButton
              workspaceId={workspaceId}
              onNavigate={closeDrawer}
            />
            <SettingsNav
              workspaceId={workspaceId}
              activeSection={activeSection}
              isAdmin={isAdmin}
              onNavigate={closeDrawer}
              className="mt-4"
            />
          </div>
        </div>
      )}

      <aside className="hidden w-56 shrink-0 flex-col gap-4 py-6 md:flex">
        <ReturnToEditorButton workspaceId={workspaceId} />
        <SettingsNav
          workspaceId={workspaceId}
          activeSection={activeSection}
          isAdmin={isAdmin}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto py-6 md:py-8">
        <div className="w-full max-w-2xl">
          {renderSection(activeSection, workspace, workspaceId, isAdmin)}
        </div>
      </div>
    </div>
  );
}
